import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link } from "react-router-dom";
import { EditIcon } from "../../../assets/icons/Icons";
import Chip from "../../../components/Chip";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import SearchFilter from "../../../components/filters/SearchFilter";
import SelectFilter from "../../../components/filters/SelectFilter";
import ResetFilters from "../../../components/filters/ResetFilters";
import CreateSellSpecialtyItem from "../../../components/sell-specialty-items/Create";
import EditSellSpecialtyItem from "../../../components/sell-specialty-items/Edit";
import MessagePopup from "../../../components/MessagePopup";
import DataTable from "../../../components/DataTable";
import CustomTooltip from "../../../components/CustomTooltip";
import useData from "../../../hooks/useData";
import FilterSellSpecialtyItems from "../../../api/sell-specialty-items/filterSellSpecialtyItems";
import { CentsToDollars } from "../../../utils/formatPrice";
import { formatDate } from "../../../utils/formatDate";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "../../../contexts/GlobalContext";

const Index = () => {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    newItemAdded,
    setNewItemAdded,
    itemUpdated,
    setItemUpdated,
    selectedItem,
    setSelectedItem,
    showEditItem,
    setShowEditItem,
    showCreateItem,
    setShowCreateItem,
  } = useData(FilterSellSpecialtyItems);

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Settings"));
    setBreadcrumbs([t("Settings"), t("Specialty Items")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const handleEditClick = (e, item) => {
    e.preventDefault();
    setSelectedItem(item);
    setShowEditItem(true);
  };

  return (
    <Layout title={t("Settings")} breadcrumbs={t("Specialty Items")}>
      {showCreateItem && (
        <CreateSellSpecialtyItem
          onCreate={() => {
            setShowCreateItem(false);
            setNewItemAdded(true);
          }}
          onClose={() => setShowCreateItem(false)}
        />
      )}
      {newItemAdded && (
        <MessagePopup
          title={t("Specialty Item Added!")}
          message={t("The specialty item has been successfully created!")}
          onClose={() => setNewItemAdded(false)}
        />
      )}
      {showEditItem && (
        <EditSellSpecialtyItem
          specialtyItem={selectedItem}
          onClose={() => setShowEditItem(false)}
          onUpdate={() => {
            setShowEditItem(false);
            setItemUpdated(true);
          }}
        />
      )}
      {itemUpdated && (
        <MessagePopup
          title={t("Specialty Item Updated!")}
          message={t("The specialty item has been successfully updated!")}
          onClose={() => setItemUpdated(false)}
        />
      )}

      <DataTable
        filtersTop={
          <FilterContextProvider name="s">
            <SearchFilter placeholderText={t("Search by name")} />
          </FilterContextProvider>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="status">
              <SelectFilter
                options={[
                  { value: "active", label: t("Active") },
                  { value: "inactive", label: t("Inactive") },
                ]}
                placeholder={<div>{t("Status")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <button
            onClick={() => setShowCreateItem(true)}
            className="btn btn-primary btn-medium"
          >
            {t("Add Specialty Item")}
          </button>
        }
        columns={[
          { header: t("Item Name"), accessor: "name" },
          { header: t("Price"), accessor: "unit_price" },
          { header: t("Tax"), accessor: "taxable" },
          { header: t("Status"), accessor: "is_active" },
          { header: t("Last Updated"), accessor: "updated_at" },
          { header: t("Action"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        emptyDataMessage={t("No specialty items set up yet")}
        actions={(item) => (
          <Link to="#" onClick={(e) => handleEditClick(e, item)}>
            <CustomTooltip icon={<EditIcon />} message={t("Edit")} />
          </Link>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "unit_price":
              return `$${CentsToDollars(value)}`;
            case "taxable":
              return (
                <Chip
                  state={value ? "active" : "inactive"}
                  label={value ? t("Taxable") : t("Non-taxable")}
                />
              );
            case "is_active":
              return (
                <Chip
                  state={value ? "active" : "inactive"}
                  label={value ? t("Active") : t("Inactive")}
                />
              );
            case "updated_at":
              return value ? formatDate(value) : "";
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
};

export default Index;
